import { useCallback } from 'react';

import {
  isValidAssignments,
  useCreateDxfSceneMapping,
  useUpdateDxfSceneMapping,
} from '@frontend/api';
import { useDisclosure, useToast } from '@frontend/legacy-components';

import {
  toAssignmentsMap,
  toAssignmentsPayload,
} from '../session/mappingModel';
import { mappingValidationSaveBlock } from '../session/mappingValidation';
import { DxfViewerSession } from '../session/types';
import { useReadableStore } from '../session/useSessionState';

export type DxfMappingActions = {
  /** Leaves mapping mode, asking first when there are edits to discard. */
  onCancel: () => void;
  onSave: () => void;
  isSaving: boolean;
  discardDialog: {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
  };
};

/**
 * Cancel and Save for the mapping toolbar.
 *
 * Save is the only place the ephemeral draft becomes durable, matching the spec:
 * nothing reaches the server until this runs. Which verb depends on whether the
 * drawing already has a mapping row — the backend refuses a second POST — so the
 * decision is made from `saved` rather than guessed at.
 */
export const useDxfMappingActions = (
  session: DxfViewerSession,
): DxfMappingActions => {
  const toast = useToast();
  const partFileUuid = useReadableStore(session.partFileUuid);
  const mapping = useReadableStore(session.mapping);
  const isDirty = useReadableStore(session.hasUnsavedMapping);
  const validation = useReadableStore(session.mappingValidation);

  const createMapping = useCreateDxfSceneMapping();
  const updateMapping = useUpdateDxfSceneMapping();
  const {
    isOpen: isDiscardDialogOpen,
    onOpen: onOpenDiscardDialog,
    onClose: onCloseDiscardDialog,
  } = useDisclosure();

  const leaveMapping = useCallback(() => {
    session.mapping.revert();
    session.mode.set('view');
  }, [session]);

  const onCancel = useCallback(() => {
    // Leaving with nothing to lose is immediate — the spec is explicit that only a
    // cancel with edits is worth interrupting someone for.
    if (isDirty) {
      onOpenDiscardDialog();
      return;
    }
    leaveMapping();
  }, [isDirty, onOpenDiscardDialog, leaveMapping]);

  const onConfirmDiscard = useCallback(() => {
    onCloseDiscardDialog();
    leaveMapping();
  }, [onCloseDiscardDialog, leaveMapping]);

  const onSave = useCallback(() => {
    const draft = mapping.draft;
    if (!partFileUuid || !draft || mapping.lockReason !== undefined) {
      return;
    }

    const assignments = toAssignmentsPayload(draft);
    // Pre-flighted with the same rule the write validator applies, so a malformed
    // draft is caught here rather than as a failed round trip.
    if (!isValidAssignments(assignments)) {
      toast({
        title: 'Invalid mapping structure',
        description: 'Please check your mapping and try again.',
        status: 'error',
      });
      return;
    }

    // Should already be blocked by the toolbar; keep the same copy so a stray
    // Save does not claim there are issues when the check is only pending.
    const validationBlock = mappingValidationSaveBlock(validation);
    if (validationBlock) {
      toast({
        title: validationBlock,
        status: 'error',
      });
      return;
    }

    const variables = { partFileUuid, assignments };
    const mutation = mapping.saved === null ? createMapping : updateMapping;

    mutation.mutate(variables, {
      onSuccess: (saved) => {
        // The server's copy, not the draft — what the session believes is saved
        // should be what was actually stored.
        session.mapping.commitSaved(toAssignmentsMap(saved.assignments));
        session.mode.set('view');
      },
      onError: (error) => {
        console.error(error);
        toast({
          title: 'Failed to save mapping',
          status: 'error',
        });
      },
    });
  }, [
    session,
    partFileUuid,
    mapping.draft,
    mapping.saved,
    mapping.lockReason,
    validation,
    createMapping,
    updateMapping,
    toast,
  ]);

  return {
    onCancel,
    onSave,
    isSaving: createMapping.isPending || updateMapping.isPending,
    discardDialog: {
      isOpen: isDiscardDialogOpen,
      onClose: onCloseDiscardDialog,
      onConfirm: onConfirmDiscard,
    },
  };
};
